<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div ref="exportTarget">
        <div ref="exportHeader" class="export-header" data-testid="export-header">
          <div>{{ placeName }} / {{ selectedMonth }}</div>
          <div>{{ floorPlanName }}</div>
        </div>
        <v-list dense data-testid="insect-ranking-list">
          <v-list-item
            v-for="(insect, index) in topInsects"
            :key="insect.type"
            class="ranking-item"
          >
            <v-list-item-icon class="ranking-number-icon">
              <span class="ranking-badge" :style="{ color: getRankColor(index) }">
                【{{ index + 1 }}】
              </span>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ insect.label }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('captureCount', { count: insect.count }) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="insect.type !== 'others' && !isExporting">
              <icon-button
                small
                icon="info"
                data-testid="insect-info-button"
                @click="showInsectInfo(insect.type)"
              />
            </v-list-item-action>
          </v-list-item>
          <v-list-item v-if="topInsects.length === 0">
            <v-list-item-content>
              <v-list-item-title class="text--secondary">
                {{ $t('no_data') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </div>
    </v-card-text>
    <card-dialog v-model="insectExplanationVisible" width="600px">
      <insect-description :insect-type="insectExplanationType" />
      <template #actions>
        <dialog-action @click="insectExplanationVisible = false">{{ $t('button.close') }}</dialog-action>
      </template>
    </card-dialog>
  </v-card>
</template>

<i18n lang="yaml">
ja:
  captureCount: '捕獲数: {count}'
  no_data: データがありません
  button:
    close: '閉じる'
  export:
    ranking_type: 虫ランキング
    csv:
      place: 設置場所
      floor_plan: 図面
      period: 期間
      rank: 順位
      insect_type: 虫種名
      capture_count: 捕獲数

en:
  captureCount: 'Capture Count: {count}'
  no_data: No data available
  button:
    close: 'Close'
  export:
    ranking_type: Insect Ranking
    csv:
      place: Place
      floor_plan: Floor Plan
      period: Period
      rank: Rank
      insect_type: Insect Type
      capture_count: Capture Count
</i18n>

<script>
import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import IconButton from '@/components/atoms/IconButton';
import InsectDescription from '@/components/organisms/InsectDescription';
import { flyingInsectTypeMap } from '@/libs/insectTypes';
import {
  generateRankingCsvString,
  buildRankingFileName,
  exportRankingImage as defaultExportRankingImage,
  exportRankingCsv as defaultExportRankingCsv,
} from '../PlaceFloorPlans/rankingExport';

const RANKING_LIMIT = 5;

export default {
  name: 'InsectRanking',
  components: {
    CardDialog,
    DialogAction,
    IconButton,
    InsectDescription,
  },
  inject: {
    exportRankingImage: {
      default() {
        return defaultExportRankingImage;
      },
    },
    exportRankingCsv: {
      default() {
        return defaultExportRankingCsv;
      },
    },
  },
  props: {
    traps: { type: Array, required: true },
    events: { type: Array, required: true },
    placeName: { type: String, default: '' },
    floorPlanName: { type: String, default: '' },
    selectedMonth: { type: String, default: '' },
    isExporting: { type: Boolean, default: false },
  },
  data() {
    return {
      insectExplanationVisible: false,
      insectExplanationType: null,
    };
  },
  computed: {
    placedTrapNos() {
      return this.traps
        .filter(trap => trap.isPlaced)
        .map(trap => trap.trapNo);
    },
    rankedInsects() {
      // 配置済みトラップのイベントのみ集計
      const placedSet = new Set(this.placedTrapNos);
      const placedEvents = this.events.filter(
        event => placedSet.has(event.trap_no),
      );

      // 虫種別に集計
      const insectCounts = {};
      placedEvents.forEach(event => {
        Object.entries(event.insect_counts || {}).forEach(([type, count]) => {
          insectCounts[type] = (insectCounts[type] || 0) + count;
        });
      });

      // ランキング形式に変換（全件ソート済み）
      const insectTypes = flyingInsectTypeMap(this.$i18n.locale);
      return Object.entries(insectCounts)
        .map(([type, count]) => ({
          type,
          label: insectTypes[type]?.label || type,
          count,
        }))
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.type.localeCompare(b.type); // 同点時は type 順
        });
    },
    topInsects() {
      return this.rankedInsects.slice(0, RANKING_LIMIT);
    },
  },
  methods: {
    showInsectInfo(insectType) {
      this.insectExplanationVisible = true;
      this.insectExplanationType = insectType;
    },
    getRankColor(index) {
      // 上位ほど要注意を示す警告色（視認性を確保した濃いめのトーン）
      const colors = ['#d32f2f', '#ef6c00', '#9e9d24', '#757575', '#757575'];
      return colors[index] || '#757575';
    },
    async exportImage() {
      const header = this.$refs.exportHeader;
      header.style.display = 'block';
      await this.$nextTick();
      try {
        const fileName = buildRankingFileName(
          this.placeName,
          this.floorPlanName,
          this.selectedMonth,
          this.$t('export.ranking_type'),
          'png',
        );
        await this.exportRankingImage(this.$refs.exportTarget, fileName);
      } finally {
        header.style.display = '';
      }
    },
    exportCsv() {
      const metaRows = [
        [this.$t('export.csv.place'), this.placeName],
        [this.$t('export.csv.floor_plan'), this.floorPlanName],
        [this.$t('export.csv.period'), this.selectedMonth],
      ];
      const headers = [
        this.$t('export.csv.rank'),
        this.$t('export.csv.insect_type'),
        this.$t('export.csv.capture_count'),
      ];
      const dataRows = this.rankedInsects.map((insect, index) => [
        index + 1, insect.label, insect.count,
      ]);
      const csvString = generateRankingCsvString(metaRows, headers, dataRows);
      const fileName = buildRankingFileName(
        this.placeName,
        this.floorPlanName,
        this.selectedMonth,
        this.$t('export.ranking_type'),
        'csv',
      );
      this.exportRankingCsv(csvString, fileName);
    },
  },
};
</script>

<style scoped>
.ranking-number-icon {
  min-width: 40px;
}

.ranking-badge {
  font-weight: bold;
  font-size: 1.1em;
  white-space: nowrap;
}

.ranking-item {
  border-bottom: 1px solid #e0e0e0;
}

.ranking-item:last-child {
  border-bottom: none;
}

.export-header {
  display: none;
  padding: 8px 16px;
}

.export-header div {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
